/**
 * TesoreriaPage — gestione cassa reale della Lega.
 *
 * Accesso ristretto: solo Presidente Lega / Admin / Tesoriere.
 *
 * Sezioni:
 * 1. Quota iscrizione (input box, salvata in localStorage)
 * 2. Checklist 8 squadre: spunta = registra pagamento iscrizione → entrata cassa
 * 3. Bottone "Aggiungi spesa": form importo negativo + descrizione → uscita cassa
 * 4. Saldo cassa corrente
 * 5. Storico movimenti (entrate + uscite con descrizione)
 *
 * Il reset della checklist a nuova stagione è implicito: i pagamenti sono
 * registrati per stagione_id, quindi una nuova stagione = checklist vuota.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneCorrente } from "../../hooks/useStagione";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const ALLOWED_ROLES = new Set(["presidente_lega", "admin", "tesoriere"]);

const QUOTA_LS_KEY = "fantasquad.tesoreria.quota_iscrizione_eur";

interface IscrizioneItem {
  squadra_id: string;
  squadra_nome: string;
  presidente_user_id: string | null;
  pagata: boolean;
  importo_pagato: string | null;
}

interface MovimentoItem {
  id: string;
  importo_euro: string;
  tipo: string;
  descrizione: string;
  data_movimento: string;
  user_id: string | null;
}

interface SaldoResponse {
  stagione_id: string;
  saldo_euro: string;
  num_movimenti: number;
  ultimi_movimenti: MovimentoItem[];
}

function fmtEur(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return String(s);
  return n.toLocaleString(localeCorrente(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });
}

function userHasAccess(user: ReturnType<typeof useAuth>["user"]): boolean {
  if (!user) return false;
  const roles = new Set<string>([user.ruolo_lega ?? ""]);
  for (const r of user.ruoli_aggiuntivi ?? []) roles.add(r);
  for (const r of roles) if (ALLOWED_ROLES.has(r)) return true;
  return false;
}

export function TesoreriaPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: stagioneAttiva } = useStagioneCorrente();
  const stagioneId = stagioneAttiva?.id ?? null;

  const hasAccess = userHasAccess(user);

  // Quota iscrizione (localStorage per ricordare l'ultima impostata)
  const [quotaStr, setQuotaStr] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(QUOTA_LS_KEY) ?? "";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(QUOTA_LS_KEY, quotaStr);
    }
  }, [quotaStr]);

  // Modal spesa
  const [showSpesaModal, setShowSpesaModal] = useState(false);
  const [spesaImporto, setSpesaImporto] = useState("");
  const [spesaDesc, setSpesaDesc] = useState("");

  const { data: saldo } = useQuery({
    queryKey: ["tesoreria-saldo", stagioneId],
    queryFn: async () => (await api.get<SaldoResponse>(`/tesoreria/${stagioneId}`)).data,
    enabled: hasAccess && !!stagioneId,
  });

  const { data: iscrizioni } = useQuery({
    queryKey: ["tesoreria-iscrizioni", stagioneId],
    queryFn: async () =>
      (await api.get<IscrizioneItem[]>(`/tesoreria/${stagioneId}/iscrizioni`)).data,
    enabled: hasAccess && !!stagioneId,
  });

  const { data: movimenti } = useQuery({
    queryKey: ["tesoreria-movimenti", stagioneId],
    queryFn: async () =>
      (await api.get<MovimentoItem[]>(`/tesoreria/${stagioneId}/movimenti`)).data,
    enabled: hasAccess && !!stagioneId,
  });

  const registraIscrizioneMut = useMutation({
    mutationFn: async (squadra_id: string) => {
      const importo = Number(quotaStr);
      if (!Number.isFinite(importo) || importo <= 0) {
        throw new Error(tr("Imposta una quota iscrizione valida (> 0)"));
      }
      await api.post(`/tesoreria/${stagioneId}/iscrizione`, {
        squadra_id,
        importo_euro: importo,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tesoreria-saldo", stagioneId] });
      qc.invalidateQueries({ queryKey: ["tesoreria-iscrizioni", stagioneId] });
      qc.invalidateQueries({ queryKey: ["tesoreria-movimenti", stagioneId] });
    },
  });

  const registraSpesaMut = useMutation({
    mutationFn: async () => {
      // Andrea 2026-09-08: è una SPESA, quindi l'importo è sempre
      // un'uscita — il tastierino numerico di iOS/Android non ha il
      // meno, quindi accettiamo il numero positivo e lo rendiamo
      // negativo noi (e se qualcuno scrive -10.50 va bene uguale).
      const inserito = Number(spesaImporto);
      if (!Number.isFinite(inserito) || inserito === 0) {
        throw new Error(tr("Inserisci l'importo della spesa"));
      }
      const importo = -Math.abs(inserito);
      if (!spesaDesc.trim()) throw new Error(tr("Descrizione obbligatoria"));
      await api.post(`/tesoreria/${stagioneId}/spesa`, {
        importo_euro: importo,
        descrizione: spesaDesc.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tesoreria-saldo", stagioneId] });
      qc.invalidateQueries({ queryKey: ["tesoreria-movimenti", stagioneId] });
      setShowSpesaModal(false);
      setSpesaImporto("");
      setSpesaDesc("");
    },
  });

  if (!hasAccess) {
    return (
      <MobileShell logoSuffix="TESORERIA">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Tesoreria")}</h2>
        <div className="card text-center p-8">
          <div className="text-4xl mb-2">🔒</div>
          <div className="text-fg-muted">
            {tr("Accesso riservato a Presidente Lega, Admin e Tesoriere.")}
          </div>
        </div>
      </MobileShell>
    );
  }

  if (!stagioneAttiva) {
    return (
      <MobileShell logoSuffix="TESORERIA">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Tesoreria")}</h2>
        <div className="card text-center p-8">
          <div className="text-4xl mb-2">📅</div>
          <div className="text-fg-muted">{tr("Nessuna stagione attiva.")}</div>
        </div>
      </MobileShell>
    );
  }

  const allMovimenti = movimenti ?? [];

  return (
    <MobileShell logoSuffix="TESORERIA">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Tesoreria")}</h2>
        <div className="text-sm text-fg-muted">
          {tr("Stagione")} <span className="text-fg font-bold">{stagioneAttiva.anno}</span>
        </div>
      </div>

      {/* Saldo + bottone spesa */}
      <div className="card flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wider">{tr("Saldo cassa")}</div>
          <div
            className={`font-display text-4xl ${
              Number(saldo?.saldo_euro ?? 0) >= 0 ? "text-success" : "text-danger"
            }`}
          >
            {fmtEur(saldo?.saldo_euro)}
          </div>
          <div className="text-xs text-fg-muted mt-1">{saldo?.num_movimenti ?? 0} movimenti</div>
        </div>
        <button
          type="button"
          onClick={() => setShowSpesaModal(true)}
          className="btn-primary text-sm border border-danger/60 bg-danger/20 text-danger hover:bg-danger/30"
        >
          {tr("− Aggiungi spesa")}
        </button>
      </div>

      {/* Quota iscrizione */}
      <div className="card space-y-2">
        <label className="block text-xs text-fg-muted uppercase tracking-wider">
          {tr("Quota iscrizione (€) — applicata quando spunti una squadra")}
        </label>
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={quotaStr}
          onChange={(e) => setQuotaStr(e.target.value)}
          placeholder={tr("es. 50.00")}
          className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {/* Checklist squadre */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-2 bg-primary-deep/40 text-xs uppercase tracking-wider text-fg-muted">
          Iscrizioni squadre (spunta = registra pagamento)
        </div>
        {!iscrizioni && <div className="p-4 text-fg-muted text-sm">{tr("Caricamento…")}</div>}
        {iscrizioni && iscrizioni.length === 0 && (
          <div className="p-4 text-fg-muted text-sm">{tr("Nessuna squadra.")}</div>
        )}
        {iscrizioni?.map((it) => (
          <label
            key={it.squadra_id}
            className={`flex items-center gap-3 p-3 border-b border-primary/10 last:border-b-0 ${
              it.pagata ? "" : "cursor-pointer hover:bg-primary-soft/20"
            }`}
          >
            <input
              type="checkbox"
              checked={it.pagata}
              disabled={
                it.pagata || registraIscrizioneMut.isPending || it.presidente_user_id === null
              }
              onChange={() => {
                if (!it.pagata) registraIscrizioneMut.mutate(it.squadra_id);
              }}
              className="w-5 h-5 accent-accent"
            />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-fg">{it.squadra_nome}</div>
              {it.presidente_user_id === null && (
                <div className="text-[10px] text-warning">{tr("⚠ Squadra senza presidente")}</div>
              )}
            </div>
            {it.pagata && it.importo_pagato && (
              <span className="text-sm text-success font-bold">{fmtEur(it.importo_pagato)}</span>
            )}
          </label>
        ))}
      </div>

      {registraIscrizioneMut.isError && (
        <div className="card bg-danger/10 border-danger/30 text-sm text-danger">
          {(registraIscrizioneMut.error as Error).message}
        </div>
      )}

      {/* Storico movimenti */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-2 bg-primary-deep/40 text-xs uppercase tracking-wider text-fg-muted">
          Storico movimenti ({allMovimenti.length})
        </div>
        {allMovimenti.length === 0 && (
          <div className="p-4 text-fg-muted text-sm">{tr("Nessun movimento registrato.")}</div>
        )}
        {allMovimenti.map((m) => (
          <div
            key={m.id}
            className="flex items-center gap-3 p-3 border-b border-primary/10 last:border-b-0 text-sm"
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{m.descrizione}</div>
              <div className="text-[10px] text-fg-muted font-mono mt-0.5">
                {m.data_movimento} · {etichetta(m.tipo)}
              </div>
            </div>
            <div
              className={`font-mono font-bold ${
                Number(m.importo_euro) >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {Number(m.importo_euro) >= 0 ? "+" : ""}
              {fmtEur(m.importo_euro)}
            </div>
          </div>
        ))}
      </div>

      {/* Modal SPESA */}
      {showSpesaModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-bg-card border border-danger/40 rounded-card max-w-md w-full p-6 space-y-4">
            <h3 className="font-display text-xl text-danger">{tr("Aggiungi spesa")}</h3>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Importo € della spesa")}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-danger font-bold pointer-events-none">
                  −
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={spesaImporto}
                  onChange={(e) => setSpesaImporto(e.target.value)}
                  placeholder="10.50"
                  className="w-full pl-7 pr-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
                />
              </div>
              <div className="text-[10px] text-fg-muted mt-1">
                {tr("Scrivi solo la cifra: viene registrata come uscita dalla cassa.")}
              </div>
            </div>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Descrizione")}
              </label>
              <input
                type="text"
                value={spesaDesc}
                onChange={(e) => setSpesaDesc(e.target.value)}
                placeholder={tr("Es. Cena di fine campionato")}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
              />
            </div>
            {registraSpesaMut.isError && (
              <div className="text-danger text-xs">{(registraSpesaMut.error as Error).message}</div>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowSpesaModal(false)}
                className="btn-ghost text-sm"
                disabled={registraSpesaMut.isPending}
              >
                {tr("Annulla")}
              </button>
              <button
                onClick={() => registraSpesaMut.mutate()}
                disabled={registraSpesaMut.isPending || !spesaImporto || !spesaDesc.trim()}
                className="btn-primary text-sm border border-danger/60 bg-danger/20 text-danger hover:bg-danger/30 disabled:opacity-50"
              >
                {registraSpesaMut.isPending ? "…" : tr("Registra spesa")}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileShell>
  );
}
